/** Report-lock status hook.
 *
 * A session's report stays locked until its evidence is confirmed on the
 * blockchain (every configured anchor backend). This polls the backend's
 * anchor-status endpoint and stops polling once the report is released.
 */
import { useQuery } from "@tanstack/react-query";

import { ApiPaths, api } from "@/lib/api";
import { formatTime } from "@/lib/format";

export type ReportAnchorBackend = {
  backend: string;
  is_synchronous: boolean;
  state: "anchored" | "pending" | "missing";
};

export type ReportAnchorStatus = {
  ready: boolean;
  finalized: boolean;
  final_anchor_sequence: number | null;
  backends: ReportAnchorBackend[];
  pending_backends: string[];
  next_batch_eta: string | null;
  legacy?: boolean;
};

/** Format an ISO datetime to a short local time (e.g. "14:20"). Returns null
 * when the input is missing or unparseable. */
export function formatEtaTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return formatTime(iso, { hour: "2-digit", minute: "2-digit" }) || null;
}

export function useReportAnchorStatus(sessionId: string) {
  return useQuery({
    queryKey: ["report-anchor-status", sessionId],
    queryFn: async () => {
      const res = await api.get<ReportAnchorStatus>(
        ApiPaths.sessionReportAnchorStatus(sessionId),
      );
      return res.data;
    },
    // Poll while locked; stop once the report is released.
    refetchInterval: (query) => (query.state.data?.ready ? false : 60_000),
    staleTime: 30_000,
  });
}
